'use client'

import ScrollLoadMore from '@/components/scroll-load-more'
import React, { FC, ReactElement } from 'react'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import Wallet from '@/utils/wallet'
import useWalletBalance from '@/utils/hooks/useWalletBalance'
import useBrc20Balance from '@/utils/hooks/useBrc20Balance'
import useExchangeRate from '@/utils/hooks/useExchangeRate'
import useTokenPrices from '@/utils/hooks/useTokenPrices'
import { getSatoshiPriceUSD } from '@/utils/hooks/useSatoshiPriceUSD'
import useSwr from 'swr'
import { fetcher } from '@/utils/fetcher'
import { API_URL, NAMES_SLUGS, COLLECTIONS_VERSION } from '@/utils/constants'
import formatNumber from '@/utils/format-number'
import isRune from '@/utils/is-rune'
import isAlkanes from '@/utils/is-alkanes'
import alkanesPathForSlug from '@/utils/alkanes-route'
import { useRouter } from 'next/navigation'
import useAlkanesPrices from '@/utils/hooks/useAlkanesPrices'
import { lowerQuote, useMarks } from '@/lib/quotes/hooks'
import isFungible from '@/utils/is-fungible'
import CollectionIcon from '@/components/collection-icon'
import InscriptionContent from '@/components/inscription-content'
import Event from '@/utils/event'
import CollectionDetail from '@/components/search-input/pages/collection-detail'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import groupBy from '@/utils/group-by'
import VerifiedBadge from '@/components/verified-badge'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'

// Format USD with commas
const formatUSD = (value: number | string): string => {
	const num = typeof value === 'string' ? parseFloat(value) : value
	if (isNaN(num)) return '0.00'
	return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Icons
const SearchIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<circle cx="11" cy="11" r="8" />
		<path d="m21 21-4.3-4.3" />
	</svg>
)

const GridIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<rect width="7" height="7" x="3" y="3" rx="1" />
		<rect width="7" height="7" x="14" y="3" rx="1" />
		<rect width="7" height="7" x="14" y="14" rx="1" />
		<rect width="7" height="7" x="3" y="14" rx="1" />
	</svg>
)

const ListIcon = () => (
	<svg
		xmlns="http://www.w3.org/2000/svg"
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<line x1="8" x2="21" y1="6" y2="6" />
		<line x1="8" x2="21" y1="12" y2="12" />
		<line x1="8" x2="21" y1="18" y2="18" />
		<line x1="3" x2="3.01" y1="6" y2="6" />
		<line x1="3" x2="3.01" y1="12" y2="12" />
		<line x1="3" x2="3.01" y1="18" y2="18" />
	</svg>
)

const SortIcon = () => (
	<svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M4.93 5.43C4.76 5.61 4.76 5.89 4.93 6.07C5.11 6.24 5.39 6.24 5.57 6.07L7.5 4.14L9.43 6.07C9.61 6.24 9.89 6.24 10.07 6.07C10.24 5.89 10.24 5.61 10.07 5.43L7.82 3.18C7.73 3.1 7.62 3.05 7.5 3.05C7.38 3.05 7.27 3.1 7.18 3.18L4.93 5.43ZM10.07 9.57C10.24 9.39 10.24 9.11 10.07 8.93C9.89 8.76 9.61 8.76 9.43 8.93L7.5 10.86L5.57 8.93C5.39 8.76 5.11 8.76 4.93 8.93C4.76 9.11 4.76 9.39 4.93 9.57L7.18 11.82C7.36 11.99 7.64 11.99 7.82 11.82L10.07 9.57Z"
			fill="currentColor"
			fillRule="evenodd"
			clipRule="evenodd"
		></path>
	</svg>
)

type MainTab = 'nft' | 'tokens' | 'sns' | 'sold'
type NFTSortOption = 'floor-high' | 'floor-low' | 'volume-high' | 'number-high' | 'number-low'
type TokenSortOption = 'value-high' | 'value-low' | 'volume-high' | 'balance-high'
type ViewMode = 'list' | 'grid'
type ListingFilter = 'all' | 'listed' | 'unlisted'
type SidebarSort = 'volume' | 'count' | 'floor'

const ITEMS_PER_PAGE = 96
const TRANSFER_INSCRIPTION_SLUGS = ['brc-20', 'tap', 'rune']

interface WalletItemsSectionProps {
	isMobile: boolean
}

const WalletItemsSection: FC<WalletItemsSectionProps> = ({ isMobile: _isMobile }): ReactElement => {
	const walletAddress = Wallet.getTaprootAddress()
	const openInscriptionModal = useInscriptionModal()

	// State
	const [mainTab, setMainTab] = React.useState<MainTab>('nft')
	const [viewMode, setViewMode] = React.useState<ViewMode>(() => {
		if (typeof window !== 'undefined') {
			const saved = localStorage.getItem('wallet-view-mode')
			if (saved === 'grid' || saved === 'list') return saved
		}
		return 'grid' // Default to grid view
	})
	const [searchQuery, setSearchQuery] = React.useState('')
	const [nftSortOption, setNftSortOption] = React.useState<NFTSortOption>('floor-high')
	const [tokenSortOption, setTokenSortOption] = React.useState<TokenSortOption>('value-high')
	const [selectedCollection, setSelectedCollection] = React.useState<string | null>(null)
	const [selectedProtocol, setSelectedProtocol] = React.useState<string | null>(null)
	const [listingFilter, setListingFilter] = React.useState<ListingFilter>('all')
	const [sidebarSort, setSidebarSort] = React.useState<SidebarSort>('volume')
	const [displayLimit, setDisplayLimit] = React.useState(ITEMS_PER_PAGE)
	const [lightboxOpen, setLightboxOpen] = React.useState(false)
	const [lightboxItem, setLightboxItem] = React.useState<any>(null)

	// Data hooks
	const { data: walletData, isLoading: isLoadingWallet } = useWalletBalance()
	const walletInscriptions = walletData?.inscriptions
	const { data: brc20Data, isLoading: isLoadingBrc20 } = useBrc20Balance()

	// Sold items
	const { data: soldData, isLoading: isLoadingSold } = useSwr(
		walletAddress ? `${API_URL}/wallet/${walletAddress}/inscriptions/activity?status=Sold` : null,
		fetcher
	)
	const { exchangeRate } = useExchangeRate()
	const router = useRouter()
	const { data: tokenPrices } = useTokenPrices()
	const { priceForSlug: alkanesPriceForSlug } = useAlkanesPrices()

	// Volume data for sorting
	const { data: collectionsData } = useSwr(
		`${API_URL}/${COLLECTIONS_VERSION}?limit=500&order=VolumeTotalDesc`,
		fetcher
	)

	const volumeMap = React.useMemo(() => {
		const map = new Map<string, number>()
		if (collectionsData?.collections) {
			collectionsData.collections.forEach((c: any) => {
				if (c?.slug && c?.volume_all_time) {
					map.set(c.slug, c.volume_all_time)
				}
			})
		}
		return map
	}, [collectionsData])

	// Save view mode to localStorage
	React.useEffect(() => {
		localStorage.setItem('wallet-view-mode', viewMode)
	}, [viewMode])

	// Reset display limit when tab or filters change
	React.useEffect(() => {
		requestAnimationFrame(() => {
			setDisplayLimit(ITEMS_PER_PAGE)
		})
	}, [
		mainTab,
		searchQuery,
		selectedCollection,
		selectedProtocol,
		nftSortOption,
		tokenSortOption,
		listingFilter
	])

	// Process NFT inscriptions (excluding SNS, fungibles, and used transfer inscriptions)
	const nftInscriptions = React.useMemo(() => {
		if (!walletInscriptions) return []
		return walletInscriptions.filter(
			(insc: any) =>
				!NAMES_SLUGS.includes(insc?.collection?.slug) &&
				!isFungible(insc?.collection?.slug) &&
				!TRANSFER_INSCRIPTION_SLUGS.includes(insc?.collection?.slug)
		)
	}, [walletInscriptions])

	// Process SNS inscriptions
	const snsInscriptions = React.useMemo(() => {
		if (!walletInscriptions) return []
		return walletInscriptions.filter((insc: any) =>
			NAMES_SLUGS.includes(insc?.collection?.slug)
		)
	}, [walletInscriptions])

	// Process sold items
	const soldItems = React.useMemo(() => {
		if (!soldData || !Array.isArray(soldData)) return []
		return [...soldData].sort(
			(a: any, b: any) =>
				new Date(b?.timestamp || 0).getTime() - new Date(a?.timestamp || 0).getTime()
		)
	}, [soldData])

	// Process tokens with pricing (filter out 0 balances)
	// What a holding is worth: the fair value or the floor, whichever is lower
	// (the rule the dashboard total uses) -- per unit for tokens, per item for
	// inscriptions.
	const heldSlugs = React.useMemo(() => {
		const set = new Set<string>()
		for (const c of brc20Data || []) if (c?.collection?.slug) set.add(c.collection.slug)
		for (const i of nftInscriptions || []) if (i?.collection?.slug) set.add(i.collection.slug)
		return [...set]
	}, [brc20Data, nftInscriptions])
	const marks = useMarks(heldSlugs)
	const worthOf = React.useCallback(
		(slug: string | undefined, floor: number | null | undefined) =>
			lowerQuote(slug ? marks[slug]?.fair_sats : null, Number(floor) || null) || 0,
		[marks]
	)

	const processedTokens = React.useMemo(() => {
		if (!brc20Data) return []
		return brc20Data
			.filter((c: any) => {
				// Filter out tokens with 0 balance
				const balance = c?.overall_balance || c?.amount || 0
				return balance > 0
			})
			.map((c: any) => {
				let satoshiPrice = 0
				let balanceUSD = '0.00'
				const priceCollection = (tokenPrices || []).find((e: any) => e.slug === c?.collection?.slug)

				// Alkanes are AMM-traded: their value comes from the pool price,
				// never from the marketplace floor, where one stale ask priced
				// DIESEL 10.6x over its real price and most alkanes (CheekyB
				// among them) have no listing at all. No pool price is a real
				// answer — leave the row unpriced rather than invent a floor.
				const alkanePriceUsd = isAlkanes(c?.collection?.slug)
					? alkanesPriceForSlug(c?.collection?.slug)
					: null

				if (isAlkanes(c?.collection?.slug)) {
					const balance = Number(c?.overall_balance)
					if (alkanePriceUsd && Number.isFinite(balance) && balance > 0 && exchangeRate) {
						satoshiPrice = ((alkanePriceUsd * balance) / exchangeRate) * 1e8
						balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
					}
				} else {
					const unit = worthOf(c?.collection?.slug, priceCollection?.floor_price_per)
					if (unit && c?.overall_balance) {
						satoshiPrice = unit * c.overall_balance
						balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
					}
					if (unit && isRune(priceCollection?.slug)) {
						satoshiPrice = unit * c.amount
						balanceUSD = getSatoshiPriceUSD(satoshiPrice, exchangeRate)
					}
				}

				const volumeAllTime = volumeMap.get(c?.collection?.slug) || 0
				const slug = c?.collection?.slug || ''

				// Determine protocol type
				let protocol = 'BRC-20'
				if (isRune(slug)) {
					protocol = 'RUNE'
				} else if (isAlkanes(slug)) {
					protocol = 'ALKANES'
				} else if (slug.includes('tap-')) {
					protocol = 'TAP'
				} else if (c.type === 'pow-20') {
					protocol = 'POW-20'
				} else if (c.type) {
					protocol = c.type.toUpperCase()
				}

				// Merge collection data - keep original collection's icon if priceCollection's icon is empty
				const mergedCollection = priceCollection
					? {
							...c.collection,
							...priceCollection,
							icon: priceCollection.icon || c.collection?.icon
					  }
					: c.collection

				return {
					...c,
					collection: mergedCollection,
					balanceUSD,
					satoshiPrice,
					volumeAllTime,
					protocol
				}
			})
	}, [brc20Data, tokenPrices, alkanesPriceForSlug, exchangeRate, volumeMap, worthOf])

	// Get collections grouped by slug with volume
	const collectionsList = React.useMemo(() => {
		const collections = groupBy(nftInscriptions, (i: any) => i?.collection?.slug || 'unknown')
		const list: any[] = []

		for (const [slug, items] of Object.entries(collections)) {
			const collection = (items as any[])[0]?.collection
			const volume = volumeMap.get(slug) || 0
			const floorPrice = worthOf(slug, collection?.floor_price)
			list.push({
				slug,
				name: collection?.name || 'Unknown',
				icon: collection?.icon,
				collection,
				count: (items as any[]).length,
				volume,
				floorPrice
			})
		}

		// Sort based on sidebarSort option
		switch (sidebarSort) {
			case 'count':
				return list.sort((a, b) => b.count - a.count)
			case 'floor':
				return list.sort((a, b) => b.floorPrice - a.floorPrice)
			case 'volume':
			default:
				return list.sort((a, b) => b.volume - a.volume)
		}
	}, [nftInscriptions, volumeMap, sidebarSort, worthOf])

	// Get protocol list for tokens
	const protocolsList = React.useMemo(() => {
		const protocols = groupBy(processedTokens, (t: any) => t.protocol)
		const list: any[] = []

		for (const [protocol, items] of Object.entries(protocols)) {
			list.push({
				protocol,
				count: (items as any[]).length,
				totalValue: (items as any[]).reduce((acc, t) => acc + (t.satoshiPrice || 0), 0)
			})
		}

		return list.sort((a, b) => b.totalValue - a.totalValue)
	}, [processedTokens])

	// Get collections list for sold items
	const soldCollectionsList = React.useMemo(() => {
		const collections = groupBy(soldItems, (i: any) => i?.collection?.slug || 'unknown')
		const list: any[] = []

		for (const [slug, items] of Object.entries(collections)) {
			const collection = (items as any[])[0]?.collection
			const totalSales = (items as any[]).reduce(
				(acc, i) => acc + (i?.escrow?.satoshi_price || 0),
				0
			)
			list.push({
				slug,
				name: collection?.name || 'Unknown',
				collection,
				count: (items as any[]).length,
				totalSales
			})
		}

		return list.sort((a, b) => b.totalSales - a.totalSales)
	}, [soldItems])

	// Filter and sort data based on current tab
	const displayData = React.useMemo(() => {
		let data: any[] = []

		if (mainTab === 'nft') {
			data = [...nftInscriptions]

			// Filter by selected collection
			if (selectedCollection) {
				data = data.filter((i: any) => {
					const itemSlug = i?.collection?.slug || 'unknown'
					return itemSlug === selectedCollection
				})
			}

			// Filter by listing status
			if (listingFilter === 'listed') {
				data = data.filter((i: any) => i?.escrow?.satoshi_price)
			} else if (listingFilter === 'unlisted') {
				data = data.filter((i: any) => !i?.escrow?.satoshi_price)
			}

			// Search filter
			if (searchQuery) {
				const query = searchQuery.toLowerCase()
				data = data.filter(
					(i: any) =>
						i?.collection?.name?.toLowerCase().includes(query) ||
						i?.meta?.name?.toLowerCase().includes(query) ||
						(i?.num || i?.number)?.toString().includes(query) ||
						i?.id?.toLowerCase().includes(query)
				)
			}

			// Sort
			switch (nftSortOption) {
				case 'floor-high':
					data.sort(
						(a, b) =>
							worthOf(b?.collection?.slug, b?.collection?.floor_price) -
							worthOf(a?.collection?.slug, a?.collection?.floor_price)
					)
					break
				case 'floor-low':
					data.sort(
						(a, b) =>
							worthOf(a?.collection?.slug, a?.collection?.floor_price) -
							worthOf(b?.collection?.slug, b?.collection?.floor_price)
					)
					break
				case 'volume-high':
					data.sort(
						(a, b) =>
							(volumeMap.get(b?.collection?.slug) || 0) - (volumeMap.get(a?.collection?.slug) || 0)
					)
					break
				case 'number-high':
					data.sort((a, b) => (b?.num || b?.number || 0) - (a?.num || a?.number || 0))
					break
				case 'number-low':
					data.sort((a, b) => (a?.num || a?.number || 0) - (b?.num || b?.number || 0))
					break
			}
		} else if (mainTab === 'tokens') {
			data = [...processedTokens]

			// Filter by selected protocol
			if (selectedProtocol) {
				data = data.filter((t: any) => t.protocol === selectedProtocol)
			}

			// Filter by listing status
			if (listingFilter === 'listed') {
				data = data.filter((t: any) => !!t?.escrow?.satoshi_price)
			} else if (listingFilter === 'unlisted') {
				data = data.filter((t: any) => !t?.escrow?.satoshi_price)
			}

			// Search filter
			if (searchQuery) {
				const query = searchQuery.toLowerCase()
				data = data.filter(
					(t: any) =>
						t?.ticker?.toLowerCase().includes(query) ||
						t?.collection?.name?.toLowerCase().includes(query)
				)
			}

			// Sort tokens
			switch (tokenSortOption) {
				// Value sorts: a token with no price has an UNKNOWN value, not a
				// zero one — it goes last in both directions, ordered by balance,
				// instead of leading the "low to high" list.
				case 'value-high':
				case 'value-low': {
					const direction = tokenSortOption === 'value-high' ? -1 : 1
					const balanceOf = (t: any) => Number(t.overall_balance || t.amount || 0)
					data.sort((a, b) => {
						const av = a.satoshiPrice > 0 ? a.satoshiPrice : null
						const bv = b.satoshiPrice > 0 ? b.satoshiPrice : null
						if (av === null || bv === null) {
							if (av !== null) return -1
							if (bv !== null) return 1
							return balanceOf(b) - balanceOf(a)
						}
						return av === bv ? balanceOf(b) - balanceOf(a) : (av - bv) * direction
					})
					break
				}
				case 'volume-high':
					data.sort((a, b) => b.volumeAllTime - a.volumeAllTime)
					break
				case 'balance-high':
					data.sort(
						(a, b) => (b.overall_balance || b.amount || 0) - (a.overall_balance || a.amount || 0)
					)
					break
			}
		} else if (mainTab === 'sns') {
			data = [...snsInscriptions]

			// Search filter
			if (searchQuery) {
				const query = searchQuery.toLowerCase()
				data = data.filter((i: any) => i?.meta?.name?.toLowerCase().includes(query))
			}
		} else if (mainTab === 'sold') {
			data = [...soldItems]

			// Filter by selected collection
			if (selectedCollection) {
				data = data.filter((i: any) => {
					const itemSlug = i?.collection?.slug || 'unknown'
					return itemSlug === selectedCollection
				})
			}

			// Search filter
			if (searchQuery) {
				const query = searchQuery.toLowerCase()
				data = data.filter(
					(i: any) =>
						i?.collection?.name?.toLowerCase().includes(query) ||
						i?.meta?.name?.toLowerCase().includes(query) ||
						(i?.num || i?.number)?.toString().includes(query)
				)
			}
		}

		return data
	}, [
		worthOf,
		mainTab,
		nftInscriptions,
		processedTokens,
		snsInscriptions,
		soldItems,
		selectedCollection,
		selectedProtocol,
		searchQuery,
		nftSortOption,
		tokenSortOption,
		listingFilter,
		volumeMap
	])

	const handleItemClick = (item: any) => {
		if (mainTab === 'nft' || mainTab === 'sns') {
			openInscriptionModal({ id: item.id, inscription: item })
		} else if (mainTab === 'tokens') {
			const slug = item?.collection?.slug
			// Alkanes trade on the AMM: send their rows to the /alkanes
			// market page instead of the marketplace's list/send panel.
			const alkanesPath = alkanesPathForSlug(slug)
			if (alkanesPath) {
				router.push(alkanesPath)
				return
			}
			if (slug) {
				Event.emit('command', {
					command: {
						featured: false,
						component: CollectionDetail,
						name: isAlkanes(slug) ? item.ticker : slug?.split('-')?.[1],
						args: { slug },
						heading: 'Collections'
					}
				})
			}
		}
	}

	const handleOpenCommandModal = (item: any) => {
		if (mainTab === 'tokens') {
			const slug = item?.collection?.slug
			// Alkanes trade on the AMM: send their rows to the /alkanes
			// market page instead of the marketplace's list/send panel.
			const alkanesPath = alkanesPathForSlug(slug)
			if (alkanesPath) {
				router.push(alkanesPath)
				return
			}
			if (slug) {
				Event.emit('command', {
					command: {
						featured: false,
						component: CollectionDetail,
						name: isAlkanes(slug) ? item.ticker : slug?.split('-')?.[1],
						args: { slug },
						heading: 'Collections'
					}
				})
			}
		} else {
			Event.emit('command', {
				command: {
					featured: false,
					component: CollectionDetail,
					name: item?.collection?.name || `#${item?.num || item?.number || 'Unknown'}`,
					args: {
						slug: item?.collection?.slug,
						tab: item?.escrow?.satoshi_price ? 'delist' : 'list',
						inscriptionId: item.id
					},
					heading: item?.escrow?.satoshi_price ? 'Manage Listing' : 'List for Sale'
				}
			})
		}
	}

	const isLoading =
		mainTab === 'nft'
			? isLoadingWallet
			: mainTab === 'tokens'
			? isLoadingBrc20
			: mainTab === 'sold'
			? isLoadingSold
			: isLoadingWallet
	const visibleData = displayData.slice(0, displayLimit)
	const hasMore = displayData.length > displayLimit

	return (
		<div className="w-full">
			{/* Header with Tabs, Search, View Toggle, Sort - Vercel style */}
			<div className="border-b border-border sticky top-0 z-10 mt-8 md:mt-16 bg-background">
				{/* Mobile: Search on own row */}
				<div className="block md:hidden px-4 pt-4">
					<div className="relative w-full">
						<Input
							type="text"
							placeholder={mainTab === 'tokens' ? 'Search...' : 'Search...'}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9 bg-transparent border-border text-foreground placeholder:text-muted-foreground h-9 w-full text-sm"
						/>
						<div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
							<SearchIcon />
						</div>
					</div>
				</div>

				{/* All controls in one row */}
				<div className="px-4 md:pl-0 py-3 flex items-center gap-4 flex-wrap md:flex-nowrap">
					{/* Tabs */}
					<div className="flex items-center h-9 border border-border rounded-md overflow-hidden">
						<button
							onClick={() => {
								setMainTab('nft')
								setSelectedCollection(null)
								setSelectedProtocol(null)
							}}
							className={`px-3 h-full text-xs font-medium transition-colors border-r border-border ${
 mainTab === 'nft'
 ? 'bg-white text-black'
 : 'bg-transparent text-muted-foreground hover:text-foreground'
 }`}
						>
							NFT
						</button>
						<button
							onClick={() => {
								setMainTab('tokens')
								setSelectedCollection(null)
								setSelectedProtocol(null)
							}}
							className={`px-3 h-full text-xs font-medium transition-colors border-r border-border ${
 mainTab === 'tokens'
 ? 'bg-white text-black'
 : 'bg-transparent text-muted-foreground hover:text-foreground'
 }`}
						>
							Tokens
						</button>
						<button
							onClick={() => {
								setMainTab('sns')
								setSelectedCollection(null)
								setSelectedProtocol(null)
							}}
							className={`px-3 h-full text-xs font-medium transition-colors border-r border-border ${
 mainTab === 'sns'
 ? 'bg-white text-black'
 : 'bg-transparent text-muted-foreground hover:text-foreground'
 }`}
						>
							SNS
						</button>
						<button
							onClick={() => {
								setMainTab('sold')
								setSelectedCollection(null)
								setSelectedProtocol(null)
							}}
							className={`px-3 h-full text-xs font-medium transition-colors ${
 mainTab === 'sold'
 ? 'bg-white text-black'
 : 'bg-transparent text-muted-foreground hover:text-foreground'
 }`}
						>
							Sold
						</button>
					</div>

					{/* Search - Desktop */}
					<div className="relative hidden md:block w-48">
						<Input
							type="text"
							placeholder="Search..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="pl-9 bg-transparent border-border text-foreground placeholder:text-muted-foreground h-9 text-sm"
						/>
						<div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
							<SearchIcon />
						</div>
					</div>

					{/* Sort for NFT tab */}
					{mainTab === 'nft' && (
						<>
							<Select
								value={nftSortOption}
								onValueChange={(v: NFTSortOption) => setNftSortOption(v)}
							>
								<SelectTrigger
									hideChevron
									className="w-auto h-9 bg-transparent border-border text-muted-foreground px-3 gap-2 text-sm"
								>
									<SelectValue />
									<SortIcon />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="floor-high" className="text-foreground hover:bg-accent">
										Floor: High to Low
									</SelectItem>
									<SelectItem value="floor-low" className="text-foreground hover:bg-accent">
										Floor: Low to High
									</SelectItem>
									<SelectItem value="volume-high" className="text-foreground hover:bg-accent">
										Volume: High to Low
									</SelectItem>
									<SelectItem value="number-high" className="text-foreground hover:bg-accent">
										# High to Low
									</SelectItem>
									<SelectItem value="number-low" className="text-foreground hover:bg-accent">
										# Low to High
									</SelectItem>
								</SelectContent>
							</Select>
							<Select
								value={listingFilter}
								onValueChange={(v: ListingFilter) => setListingFilter(v)}
							>
								<SelectTrigger className="w-auto h-9 bg-transparent border-border text-muted-foreground px-3 gap-2 text-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="all" className="text-foreground hover:bg-accent">
										Show All
									</SelectItem>
									<SelectItem value="listed" className="text-foreground hover:bg-accent">
										Show Listed
									</SelectItem>
									<SelectItem value="unlisted" className="text-foreground hover:bg-accent">
										Show Unlisted
									</SelectItem>
								</SelectContent>
							</Select>
						</>
					)}

					{/* Sort for Tokens tab */}
					{mainTab === 'tokens' && (
						<>
							<Select
								value={tokenSortOption}
								onValueChange={(v: TokenSortOption) => setTokenSortOption(v)}
							>
								<SelectTrigger
									hideChevron
									className="w-auto h-9 bg-transparent border-border text-muted-foreground px-3 gap-2 text-sm"
								>
									<SelectValue />
									<SortIcon />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="value-high" className="text-foreground hover:bg-accent">
										Value: High to Low
									</SelectItem>
									<SelectItem value="value-low" className="text-foreground hover:bg-accent">
										Value: Low to High
									</SelectItem>
									<SelectItem value="volume-high" className="text-foreground hover:bg-accent">
										Volume: High to Low
									</SelectItem>
									<SelectItem value="balance-high" className="text-foreground hover:bg-accent">
										Balance: High to Low
									</SelectItem>
								</SelectContent>
							</Select>
							<Select
								value={listingFilter}
								onValueChange={(v: ListingFilter) => setListingFilter(v)}
							>
								<SelectTrigger className="w-auto h-9 bg-transparent border-border text-muted-foreground px-3 gap-2 text-sm">
									<SelectValue />
								</SelectTrigger>
								<SelectContent className="bg-card border-border">
									<SelectItem value="all" className="text-foreground hover:bg-accent">
										Show All
									</SelectItem>
									<SelectItem value="listed" className="text-foreground hover:bg-accent">
										Show Listed
									</SelectItem>
									<SelectItem value="unlisted" className="text-foreground hover:bg-accent">
										Show Unlisted
									</SelectItem>
								</SelectContent>
							</Select>
						</>
					)}

					{/* Spacer for mobile to push view toggle right */}
					<div className="flex-1 md:hidden" />

					{/* View Toggle */}
					<div className="flex items-center border border-border rounded overflow-hidden">
						<button
							onClick={() => setViewMode('list')}
							className={`p-2 transition-colors ${
								viewMode === 'list'
									? 'bg-accent text-foreground'
									: 'text-muted-foreground hover:text-foreground'
							}`}
						>
							<ListIcon />
						</button>
						<button
							onClick={() => setViewMode('grid')}
							className={`p-2 transition-colors ${
								viewMode === 'grid'
									? 'bg-accent text-foreground'
									: 'text-muted-foreground hover:text-foreground'
							}`}
						>
							<GridIcon />
						</button>
					</div>
				</div>
			</div>

			{/* Mobile Filter Dropdown */}
			<div className="md:hidden px-4 py-3 border-b border-border">
				<Select
					value={selectedCollection || selectedProtocol || 'all'}
					onValueChange={(v) => {
						if (v === 'all') {
							setSelectedCollection(null)
							setSelectedProtocol(null)
						} else if (mainTab === 'tokens') {
							setSelectedProtocol(v)
						} else {
							setSelectedCollection(v)
						}
					}}
				>
					<SelectTrigger className="w-full h-9 bg-transparent border-border text-muted-foreground text-sm">
						<SelectValue placeholder={mainTab === 'tokens' ? 'All Protocols' : 'All Collections'} />
					</SelectTrigger>
					<SelectContent className="bg-card border-border max-h-64">
						<SelectItem value="all" className="text-foreground hover:bg-accent">
							{mainTab === 'tokens'
								? 'All Protocols'
								: mainTab === 'sold'
								? 'All Sold Items'
								: 'All Collections'}
						</SelectItem>
						{mainTab === 'nft' &&
							collectionsList.map((c) => (
								<SelectItem key={c.slug} value={c.slug} className="text-foreground hover:bg-accent">
									{c.name} ({c.count})
								</SelectItem>
							))}
						{mainTab === 'tokens' &&
							protocolsList.map((p) => (
								<SelectItem
									key={p.protocol}
									value={p.protocol}
									className="text-foreground hover:bg-accent"
								>
									{p.protocol} ({p.count})
								</SelectItem>
							))}
						{mainTab === 'sold' &&
							soldCollectionsList.map((c) => (
								<SelectItem key={c.slug} value={c.slug} className="text-foreground hover:bg-accent">
									{c.name} ({c.count})
								</SelectItem>
							))}
					</SelectContent>
				</Select>
			</div>

			{/* Main Content Area */}
			<div className="md:grid md:grid-cols-[256px_1fr] border-b border-border">
				{/* Left Sidebar - Desktop Only */}
				<aside className="hidden md:block border-l border-r border-border">
					<div>
						<div className="p-4">
							<div className="flex items-center justify-between mb-3">
								<h3 className="text-xs font-medium text-muted-foreground">
									{mainTab === 'tokens'
										? 'Protocols'
										: mainTab === 'sold'
										? 'Sold Collections'
										: 'Collections'}
								</h3>
								{mainTab === 'nft' && (
									<Select value={sidebarSort} onValueChange={(v: SidebarSort) => setSidebarSort(v)}>
										<SelectTrigger
											hideChevron
											className="size-auto bg-transparent border-0 text-muted-foreground text-[10px] p-0 gap-1 hover:text-foreground"
										>
											<SelectValue />
											<SortIcon />
										</SelectTrigger>
										<SelectContent className="bg-card border-border">
											<SelectItem
												value="volume"
												className="text-foreground text-xs hover:bg-accent"
											>
												Volume
											</SelectItem>
											<SelectItem value="count" className="text-foreground text-xs hover:bg-accent">
												Count
											</SelectItem>
											<SelectItem value="floor" className="text-foreground text-xs hover:bg-accent">
												Floor
											</SelectItem>
										</SelectContent>
									</Select>
								)}
							</div>

							{mainTab === 'nft' && (
								<div className="space-y-1">
									<button
										onClick={() => setSelectedCollection(null)}
										className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
											!selectedCollection
												? 'bg-accent text-foreground'
												: 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
										}`}
									>
										<span>All Collections</span>
										<span className="text-xs text-muted-foreground">{nftInscriptions.length}</span>
									</button>
									{collectionsList.map((c) => (
										<button
											key={c.slug}
											onClick={() => setSelectedCollection(c.slug)}
											className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
												selectedCollection === c.slug
													? 'bg-accent text-foreground'
													: 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
											}`}
										>
											<CollectionIcon
												collection={c.collection}
												className="size-6 rounded"
												imageClassName="size-6 rounded"
											/>
											<span className="flex-1 truncate text-left">{c.name}</span>
											<span className="text-xs text-muted-foreground">{c.count}</span>
										</button>
									))}
								</div>
							)}

							{mainTab === 'tokens' && (
								<div className="space-y-1">
									<button
										onClick={() => setSelectedProtocol(null)}
										className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
											!selectedProtocol
												? 'bg-accent text-foreground'
												: 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
										}`}
									>
										<span>All Protocols</span>
										<span className="text-xs text-muted-foreground">{processedTokens.length}</span>
									</button>
									{protocolsList.map((p) => (
										<button
											key={p.protocol}
											onClick={() => setSelectedProtocol(p.protocol)}
											className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
												selectedProtocol === p.protocol
													? 'bg-accent text-foreground'
													: 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
											}`}
										>
											<span>{p.protocol}</span>
											<span className="text-xs text-muted-foreground">{p.count}</span>
										</button>
									))}
								</div>
							)}

							{mainTab === 'sns' && (
								<div className="text-sm text-muted-foreground px-3 py-2">Your SNS names</div>
							)}

							{mainTab === 'sold' && (
								<div className="space-y-1">
									<button
										onClick={() => setSelectedCollection(null)}
										className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
											!selectedCollection
												? 'bg-accent text-foreground'
												: 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
										}`}
									>
										<span>All Sold Items</span>
										<span className="text-xs text-muted-foreground">{soldItems.length}</span>
									</button>
									{soldCollectionsList.map((c) => (
										<button
											key={c.slug}
											onClick={() => setSelectedCollection(c.slug)}
											className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
												selectedCollection === c.slug
													? 'bg-accent text-foreground'
													: 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
											}`}
										>
											<CollectionIcon
												collection={c.collection}
												className="size-6 rounded"
												imageClassName="size-6 rounded"
											/>
											<span className="flex-1 truncate text-left">{c.name}</span>
											<span className="text-xs text-muted-foreground">{c.count}</span>
										</button>
									))}
								</div>
							)}
						</div>
					</div>
				</aside>

				{/* Items List/Grid */}
				<div className="p-4 md:p-6 min-w-0 border-r border-border">
					{isLoading ? (
						<div className="space-y-4">
							{[...Array(6)].map((_, i) => (
								<Skeleton key={i} className="h-16 w-full" />
							))}
						</div>
					) : displayData.length === 0 ? (
						<div className="text-center py-16">
							<div className="text-zinc-600 mb-2">
								{mainTab === 'nft' && (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="size-12 mx-auto mb-3"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
										<circle cx="9" cy="9" r="2" />
										<path d="m21 15-3.09-3.09a2 2 0 0 0-2.83 0L6 21" />
									</svg>
								)}
								{mainTab === 'tokens' && (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="size-12 mx-auto mb-3"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<circle cx="12" cy="12" r="8" />
										<line x1="12" y1="8" x2="12" y2="12" />
										<line x1="12" y1="16" x2="12.01" y2="16" />
									</svg>
								)}
								{mainTab === 'sns' && (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="size-12 mx-auto mb-3"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
										<circle cx="12" cy="7" r="4" />
									</svg>
								)}
								{mainTab === 'sold' && (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										className="size-12 mx-auto mb-3"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="1.5"
										strokeLinecap="round"
										strokeLinejoin="round"
									>
										<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
										<path d="M3 6h18" />
										<path d="M16 10a4 4 0 0 1-8 0" />
									</svg>
								)}
							</div>
							<p className="text-muted-foreground">
								{mainTab === 'nft' &&
									(listingFilter === 'listed'
										? 'No listed NFTs'
										: listingFilter === 'unlisted'
										? 'No unlisted NFTs'
										: 'No NFTs found')}
								{mainTab === 'tokens' && 'No tokens found'}
								{mainTab === 'sns' && 'No SNS names found'}
								{mainTab === 'sold' && 'No sold items'}
							</p>
							<br />
							<p className="text-zinc-600 text-sm">
								{mainTab === 'nft' && 'Your NFT inscriptions will appear here'}
								{mainTab === 'tokens' && 'Your BRC-20, Runes, and other tokens will appear here'}
								{mainTab === 'sns' && 'Your .sats and other name inscriptions will appear here'}
								{mainTab === 'sold' && "Items you've sold will appear here"}
							</p>
						</div>
					) : viewMode === 'list' ? (
						/* List View - Compact 2-column layout */
						<div>
							{/* Table Headers */}
							<div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-4 mb-2">
								<div className="flex items-center justify-between text-[10px] text-muted-foreground px-2 pb-2 border-b border-border">
									<span>Item</span>
									<span>{mainTab === 'tokens' ? 'Balance' : 'Price'}</span>
								</div>
								<div className="hidden md:flex items-center justify-between text-[10px] text-muted-foreground px-2 pb-2 border-b border-border">
									<span>Item</span>
									<span>{mainTab === 'tokens' ? 'Balance' : 'Price'}</span>
								</div>
							</div>
							{/* Items */}
							<div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-4">
								{visibleData.map((item: any, i: number) => {
									const isListed = !!item?.escrow?.satoshi_price
									const listingBtc = isListed ? (item.escrow.satoshi_price / 1e8).toFixed(8) : null
									const worth = worthOf(item?.collection?.slug, item?.collection?.floor_price)
									const floorBtc = worth ? (worth / 1e8).toFixed(8) : null
									const floorUsd = worth ? formatUSD((worth / 1e8) * exchangeRate) : null

									return (
										<div
											key={item.id || i}
											onClick={() => handleOpenCommandModal(item)}
											className="flex items-center gap-2 p-2 cursor-pointer transition-colors hover:bg-accent/50 border-b border-border"
											role="button"
											tabIndex={0}
											onKeyDown={onKeyboardClick(() => handleOpenCommandModal(item))}
										>
											{/* Image - click opens lightbox */}
											<div
												className="size-8 rounded overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
												onClick={(e) => {
													e.stopPropagation()
													setLightboxItem(item)
													setLightboxOpen(true)
												}}
												role="button"
												tabIndex={0}
												onKeyDown={onKeyboardClick((e) => {
													e.stopPropagation()
													setLightboxItem(item)
													setLightboxOpen(true)
												})}
											>
												{mainTab === 'tokens' || isFungible(item?.collection?.slug) ? (
													<CollectionIcon
														collection={item.collection}
														className="size-8"
														imageClassName="size-8"
													/>
												) : (
													<InscriptionContent
														className="size-8 object-cover"
														inscription={item}
														smallCard={true}
													/>
												)}
											</div>

											{/* Info */}
											<div className="flex items-center gap-3 flex-1 min-w-0">
												<div className="flex flex-col min-w-0">
													<button
														onClick={(e) => {
															e.stopPropagation()
															handleItemClick(item)
														}}
														className="text-sm text-muted-foreground truncate hover:underline text-left max-w-[120px] md:max-w-[200px]"
													>
														{mainTab === 'tokens' || isFungible(item?.collection?.slug)
															? item.ticker?.toUpperCase() ||
															  item?.collection?.slug?.split('-')?.[1]?.toUpperCase() ||
															  item?.collection?.name
															: item?.meta?.name ||
															  item?.collection?.name ||
															  `#${item?.num || item?.number || 'Unknown'}`}
													</button>
													{mainTab !== 'tokens' &&
														!isFungible(item?.collection?.slug) &&
														(item?.num || item?.number) && (
															<span className="text-[10px] text-muted-foreground leading-none">
																#{item?.num || item?.number}
															</span>
														)}
													{isFungible(item?.collection?.slug) && mainTab === 'sold' && (
														<span className="text-[10px] text-muted-foreground leading-none">
															{isRune(item?.collection?.slug)
																? 'RUNE'
																: isAlkanes(item?.collection?.slug)
																? 'ALKANES'
																: item?.collection?.slug?.includes('tap-')
																? 'TAP'
																: 'BRC-20'}
														</span>
													)}
												</div>
												{isListed && mainTab !== 'sold' && (
													<span className="text-[9px] text-white bg-gradient-to-r from-purple-600 to-blue-500 px-1.5 py-0.5 rounded flex-shrink-0 self-center">
														Listed
													</span>
												)}
											</div>

											{/* Price */}
											<div className="text-right flex-shrink-0 self-center">
												{mainTab === 'tokens' ? (
													isListed ? (
														<div className="flex flex-col items-end">
															<span className="text-sm text-muted-foreground leading-none">
																{(item.escrow.satoshi_price / 1e8).toFixed(8)}
															</span>
															<span className="text-[10px] text-muted-foreground leading-none mt-0.5">
																{formatNumber(item.overall_balance || item.amount)} listed
															</span>
														</div>
													) : (
														<div className="flex flex-col items-end">
															<span className="text-sm text-muted-foreground leading-none">
																{formatNumber(item.overall_balance || item.amount)}
															</span>
															<span className="text-[10px] text-muted-foreground leading-none mt-0.5">
																${formatUSD(item.balanceUSD)}
															</span>
														</div>
													)
												) : mainTab === 'sold' && item?.escrow?.satoshi_price ? (
													<div className="flex flex-col items-end">
														<span className="text-sm text-success leading-none">
															{(item.escrow.satoshi_price / 1e8).toFixed(8)}
														</span>
														<span className="text-[10px] text-muted-foreground leading-none mt-0.5">
															${formatUSD((item.escrow.satoshi_price / 1e8) * exchangeRate)}
														</span>
													</div>
												) : isListed ? (
													<div className="flex flex-col items-end">
														<span className="text-sm text-muted-foreground leading-none">
															{listingBtc}
														</span>
														{floorBtc && (
															<span className="text-[10px] text-muted-foreground leading-none mt-0.5">
																Floor: {floorBtc}
															</span>
														)}
													</div>
												) : floorBtc ? (
													<div className="flex flex-col items-end">
														<span className="text-sm text-muted-foreground leading-none">
															~{floorBtc}
														</span>
														<span className="text-[10px] text-muted-foreground leading-none mt-0.5">
															~${floorUsd}
														</span>
													</div>
												) : (
													<span className="text-[10px] text-muted-foreground">—</span>
												)}
											</div>
										</div>
									)
								})}
							</div>
						</div>
					) : (
						/* Grid View */
						<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 md:gap-4">
							{visibleData.map((item: any, i: number) => (
								<div
									key={item.id || i}
									className={`rounded-lg border overflow-hidden transition-all duration-200 ease-in-out hover:-translate-y-0.5 hover:shadow-lg hover:shadow-black/20 ${
										item?.escrow?.satoshi_price && mainTab !== 'sold'
											? 'border-purple-500/50 bg-purple-500/5'
											: 'border-border hover:bg-accent/30'
									}`}
								>
									{/* Image - click opens command modal */}
									<div
										className="aspect-square cursor-pointer relative"
										onClick={() => handleOpenCommandModal(item)}
										role="button"
										tabIndex={0}
										onKeyDown={onKeyboardClick(() => handleOpenCommandModal(item))}
									>
										{item?.escrow?.satoshi_price && mainTab !== 'sold' && (
											<div className="absolute top-2 left-2 px-2 py-0.5 bg-purple-600 text-white text-[10px] font-bold rounded">
												LISTED
											</div>
										)}
										{mainTab === 'tokens' || isFungible(item?.collection?.slug) ? (
											<CollectionIcon
												collection={item.collection}
												className="size-full"
												imageClassName="w-full h-full"
											/>
										) : (
											<InscriptionContent
												className="size-full object-cover"
												inscription={item}
												smallCard={true}
											/>
										)}
									</div>

									{/* Info - click opens command modal */}
									<div
										className="p-3 cursor-pointer"
										onClick={() => handleOpenCommandModal(item)}
										role="button"
										tabIndex={0}
										onKeyDown={onKeyboardClick(() => handleOpenCommandModal(item))}
									>
										<button
											onClick={(e) => {
												e.stopPropagation()
												handleItemClick(item)
											}}
											className="text-sm font-medium text-foreground truncate block hover:underline text-left w-full"
										>
											{mainTab === 'tokens' || isFungible(item?.collection?.slug)
												? item.ticker?.toUpperCase() ||
												  item?.collection?.slug?.split('-')?.[1]?.toUpperCase() ||
												  item?.collection?.name
												: item?.meta?.name || `#${item?.num || item?.number || 'Unknown'}`}
										</button>
										<a
											href={
												item?.collection?.slug ? `/collection/${item.collection.slug}` : undefined
											}
											onClick={(e) => e.stopPropagation()}
											className="text-xs text-muted-foreground truncate block hover:text-purple-700 dark:hover:text-purple-400 hover:underline text-left w-full mt-0.5"
										>
											{mainTab === 'tokens' ||
											(mainTab === 'sold' && isFungible(item?.collection?.slug))
												? isRune(item?.collection?.slug)
													? 'RUNE'
													: isAlkanes(item?.collection?.slug)
													? 'ALKANES'
													: item?.collection?.slug?.includes('tap-')
													? 'TAP'
													: item.protocol || 'BRC-20'
												: item?.collection?.name || 'Inscription'}
											<VerifiedBadge slug={item?.collection?.slug} />
										</a>
										{mainTab === 'tokens' ? (
											item?.escrow?.satoshi_price ? (
												<div className="mt-2 flex flex-col gap-0.5">
													<div className="text-xs text-amber-400 font-medium">
														{(item.escrow.satoshi_price / 1e8).toFixed(6)} BTC
													</div>
													<div className="text-xs text-muted-foreground">
														{formatNumber(item.overall_balance || item.amount)} listed
													</div>
												</div>
											) : (
												<div className="mt-2 flex flex-col gap-0.5">
													<div className="text-xs text-foreground font-medium">
														{formatNumber(item.overall_balance || item.amount)}
													</div>
													<div className="text-xs text-muted-foreground">
														${formatUSD(item.balanceUSD)}
													</div>
												</div>
											)
										) : mainTab === 'sold' && item?.escrow?.satoshi_price ? (
											<div className="mt-2 flex flex-col gap-0.5">
												<div className="text-xs text-success">
													{(item.escrow.satoshi_price / 1e8).toFixed(6)} BTC
												</div>
												<div className="text-xs text-muted-foreground">
													${formatUSD((item.escrow.satoshi_price / 1e8) * exchangeRate)}
												</div>
											</div>
										) : item?.escrow?.satoshi_price ? (
											/* Listed item - show listing price + floor reference */
											<div className="mt-2 flex flex-col gap-0.5">
												<div className="text-xs text-amber-400">
													{(item.escrow.satoshi_price / 1e8).toFixed(6)} BTC
												</div>
												{item?.collection?.floor_price && (
													<div className="text-xs text-muted-foreground">
														Floor: {(item.collection.floor_price / 1e8).toFixed(6)}
													</div>
												)}
											</div>
										) : worthOf(item?.collection?.slug, item?.collection?.floor_price) ? (
											/* Unlisted item - what it is worth (fair or floor, lower) */
											<div className="mt-2 flex flex-col gap-0.5">
												<div className="text-xs text-foreground">
													~{(worthOf(item?.collection?.slug, item?.collection?.floor_price) / 1e8).toFixed(6)} BTC
												</div>
												<div className="text-xs text-muted-foreground">
													~${formatUSD((worthOf(item?.collection?.slug, item?.collection?.floor_price) / 1e8) * exchangeRate)}
												</div>
											</div>
										) : null}
									</div>

									{/* Action Buttons Footer (NFT only) */}
									{mainTab === 'nft' && (
										<div className="px-3 pb-3 flex gap-2">
											{item?.escrow?.satoshi_price ? (
												/* Listed item - show Delist */
												<button
													onClick={(e) => {
														e.stopPropagation()
														Event.emit('command', {
															command: {
																featured: false,
																component: CollectionDetail,
																name:
																	item?.collection?.name ||
																	`#${item?.num || item?.number || 'Unknown'}`,
																args: {
																	slug: item?.collection?.slug,
																	tab: 'delist',
																	inscriptionId: item.id
																},
																heading: 'Delist Item'
															}
														})
													}}
													className="flex-1 px-3 py-2 text-xs font-medium text-amber-400 bg-transparent hover:bg-amber-500/10 border border-amber-400 rounded-lg transition-colors text-center"
												>
													Delist
												</button>
											) : (
												/* Unlisted item - show List and Send */
												<>
													<button
														onClick={(e) => {
															e.stopPropagation()
															Event.emit('command', {
																command: {
																	featured: false,
																	component: CollectionDetail,
																	name:
																		item?.collection?.name ||
																		`#${item?.num || item?.number || 'Unknown'}`,
																	args: {
																		slug: item?.collection?.slug,
																		tab: 'list',
																		inscriptionId: item.id
																	},
																	heading: 'List for Sale'
																}
															})
														}}
														className="flex-1 px-3 py-2 text-xs font-medium text-white bg-purple-600 hover:bg-purple-500 rounded-lg transition-colors text-center"
													>
														List
													</button>
													<button
														onClick={(e) => {
															e.stopPropagation()
															Event.emit('command', {
																command: {
																	featured: false,
																	component: CollectionDetail,
																	name:
																		item?.collection?.name ||
																		`#${item?.num || item?.number || 'Unknown'}`,
																	args: {
																		slug: item?.collection?.slug,
																		tab: 'send',
																		inscriptionId: item.id
																	},
																	heading: 'Transfer'
																}
															})
														}}
														className="flex-1 px-3 py-2 text-xs font-medium text-muted-foreground bg-accent hover:bg-accent/80 rounded-lg transition-colors text-center"
													>
														Send
													</button>
												</>
											)}
										</div>
									)}
								</div>
							))}
						</div>
					)}

					<ScrollLoadMore
						hasMore={hasMore}
						itemCount={visibleData.length}
						onLoadMore={() => setDisplayLimit(limit => limit + ITEMS_PER_PAGE)}
					/>
				</div>
			</div>

			{/* Lightbox Dialog */}
			<Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
				<DialogContent className="sm:max-w-[500px] p-0 bg-transparent border-0">
					{lightboxItem && (
						<InscriptionContent
							className="size-full max-h-[80vh] object-contain rounded-lg"
							inscription={lightboxItem}
						/>
					)}
				</DialogContent>
			</Dialog>
		</div>
	)
}

export default WalletItemsSection
